import { onSnapshot } from "firebase/firestore";
import { auth } from "./FirebaseConfig";
import { flagReconnectNeeded } from "./connectionState";

// onSnapshot wrapper that self-heals a stale-auth failure. A permission-denied (or
// unauthenticated) error WHILE the user is still signed in is almost always an
// expired ID token after a network blip — not a real rules denial. Firestore treats
// such an error as terminal (the listener dies and never retries), so we force a
// token refresh and re-subscribe, backing off across attempts. If recovery keeps
// failing we flag the global reconnect banner so the user isn't silently stuck.
//
// Signature mirrors onSnapshot's common form and returns an unsubscribe fn.
export function resilientOnSnapshot(refOrQuery, onData, label = "snapshot") {
  let unsub = null;
  let cancelled = false;
  let attempts = 0;

  const subscribe = () => {
    if (cancelled) return;
    unsub = onSnapshot(
      refOrQuery,
      (snap) => {
        attempts = 0;
        flagReconnectNeeded(false); // data flowing again
        onData(snap);
      },
      async (err) => {
        const staleToken =
          (err.code === "permission-denied" || err.code === "unauthenticated") &&
          !!auth.currentUser;
        if (!staleToken || attempts >= 5) {
          console.error(`[${label}]`, err);
          if (staleToken) flagReconnectNeeded(true); // out of retries — prompt a manual reconnect
          return;
        }
        attempts += 1;
        try {
          await auth.currentUser.getIdToken(true); // force a fresh token
        } catch (_) {
          /* still offline; the backoff retry will try again */
        }
        setTimeout(subscribe, Math.min(1000 * attempts, 5000));
      },
    );
  };

  subscribe();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}
